import React, { PureComponent } from 'react'
import { Button } from 'react-bootstrap'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { bindActionCreators } from 'redux'
import { logoutUser } from '../actions'
import { confirmPasswordReset, requestPasswordResetCode } from '../actions/password_reset'
import { LandingLayout } from '../components/landing_page/landing_layout'
import NewPasswordFields, { isNewPasswordValid, toMessages } from '../components/new_password_fields'
import { LOGOUT_DELAY_MS } from '../components/password_reset_section'
import { RESET_PASSWORD_PATH } from '../utils/routes'

export const PHASE_REQUEST = 'request'
export const PHASE_SET_PASSWORD = 'set_password'
export const PHASE_DONE = 'done'

export const readCodeFromHash = (hash) => {
  const match = String(hash)
    .replace(/^#/, '')
    .match(/(?:^|&)code=([^&]*)/)
  if (!match) {
    return ''
  }
  try {
    return decodeURIComponent(match[1])
  } catch (error) {
    return ''
  }
}

export class ResetPassword extends PureComponent {
  constructor(props) {
    super(props)
    this.state = {
      phase: PHASE_REQUEST,
      email: '',
      code: '',
      newPassword: '',
      confirmPassword: '',
      linkSent: false,
      requestError: null,
      emailErrors: [],
      codeErrors: [],
      passwordErrors: [],
    }
    this.logoutTimer = null
    this.mounted = true
  }

  componentDidMount() {
    const code = readCodeFromHash(window.location.hash)
    if (!code) {
      return
    }
    this.setState({ phase: PHASE_SET_PASSWORD, code })
    this.props.history.replace(RESET_PASSWORD_PATH)
  }

  componentWillUnmount() {
    this.mounted = false
    if (this.logoutTimer) {
      clearTimeout(this.logoutTimer)
    }
  }

  changeHandler = ({ target }) => this.setState({ [target.name]: target.value })

  requestLink = () => {
    const { requestPasswordResetCode } = this.props
    const email = this.state.email.trim()
    this.setState({ requestError: null, emailErrors: [] })
    return requestPasswordResetCode({ email }).then(({ error, details }) => {
      if (!this.mounted) {
        return
      }
      if (error) {
        const emailErrors = toMessages(details && details.email)
        this.setState({ requestError: emailErrors.length > 0 ? null : error, emailErrors })
        return
      }
      this.setState({ linkSent: true })
    })
  }

  isSubmitEnabled = () => {
    const { newPassword, confirmPassword } = this.state
    return isNewPasswordValid(newPassword, confirmPassword)
  }

  startOver = () =>
    this.setState({
      phase: PHASE_REQUEST,
      code: '',
      newPassword: '',
      confirmPassword: '',
      linkSent: false,
      requestError: null,
      emailErrors: [],
      codeErrors: [],
      passwordErrors: [],
    })

  submit = () => {
    const { confirmPasswordReset, logoutUser } = this.props
    const { code, newPassword } = this.state
    this.setState({ requestError: null, codeErrors: [], passwordErrors: [] })
    return confirmPasswordReset({ code: code.trim(), new_password: newPassword }).then(({ error, details }) => {
      if (!this.mounted) {
        return
      }
      if (error) {
        const codeErrors = toMessages(details && details.code)
        const passwordErrors = (details && details.new_password) || []
        const hasFieldErrors = codeErrors.length > 0 || passwordErrors.length > 0
        this.setState({ requestError: hasFieldErrors ? null : error, codeErrors, passwordErrors })
        return
      }
      this.setState({ phase: PHASE_DONE, code: '', newPassword: '', confirmPassword: '' })
      this.logoutTimer = setTimeout(logoutUser, LOGOUT_DELAY_MS)
    })
  }

  renderRequestPhase() {
    const { isRequestingCode } = this.props
    const { email, linkSent, emailErrors } = this.state
    if (linkSent) {
      return (
        <p id='reset-link-sent'>
          If an account matches that address, a reset link is on its way. It may take a moment to arrive.
        </p>
      )
    }
    return (
      <div>
        <p id='reset-password-intro'>Enter your email address and we&apos;ll send you a link to reset your password.</p>
        <input
          id='reset-email'
          name='email'
          type='email'
          placeholder='Email address'
          className='form-control input-md'
          value={email}
          autoComplete='username'
          onChange={this.changeHandler}
        />
        {emailErrors.map((message, index) => (
          <div key={`${index}-${message}`} className='text-danger reset-email-error'>
            {message}
          </div>
        ))}
        <Button
          id='send-reset-link'
          variant='primary'
          disabled={isRequestingCode || email.trim().length === 0}
          onClick={this.requestLink}
        >
          Send reset link
        </Button>
      </div>
    )
  }

  renderSetPasswordPhase() {
    const { isConfirming } = this.props
    const { newPassword, confirmPassword, codeErrors, passwordErrors } = this.state
    return (
      <div>
        <NewPasswordFields
          newPassword={newPassword}
          confirmPassword={confirmPassword}
          onChange={this.changeHandler}
          errors={passwordErrors}
        />
        {codeErrors.map((message, index) => (
          <div key={`${index}-${message}`} className='text-danger reset-code-error'>
            {message}
          </div>
        ))}
        {codeErrors.length > 0 && (
          <button type='button' id='request-new-link' className='btn btn-link' onClick={this.startOver}>
            Request a new link
          </button>
        )}
        <Button
          id='set-new-password'
          variant='primary'
          disabled={isConfirming || !this.isSubmitEnabled()}
          onClick={this.submit}
        >
          Set new password
        </Button>
      </div>
    )
  }

  render() {
    const { phase, requestError } = this.state
    return (
      <LandingLayout>
        <div>
          <h3>Reset your password</h3>
          {phase === PHASE_DONE ? (
            <p id='reset-password-done' className='text-success'>
              Password updated. Taking you to sign in with your new password.
            </p>
          ) : (
            <div>
              {phase === PHASE_REQUEST && this.renderRequestPhase()}
              {phase === PHASE_SET_PASSWORD && this.renderSetPasswordPhase()}
              {requestError && (
                <div id='reset-password-error' className='alert alert-danger'>
                  {requestError}
                </div>
              )}
            </div>
          )}
        </div>
      </LandingLayout>
    )
  }
}

const mapStateToProps = (state) => ({
  isRequestingCode: state.passwordReset.isRequestingCode,
  isConfirming: state.passwordReset.isConfirming,
})

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ requestPasswordResetCode, confirmPasswordReset, logoutUser }, dispatch)

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ResetPassword))
